// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/tabs/EvidenceExecutionTab.jsx

import React from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionEmptyState from "../shared/ExecutionEmptyState";
import ExecutionContextCard from "../shared/ExecutionContextCard";
import ExecutionMetricTile from "../shared/ExecutionMetricTile";
import ExecutionActionRow from "../shared/ExecutionActionRow";

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    const text = value.map((item) => displayValue(item, "")).filter(Boolean).join(", ");
    return text || fallback;
  }
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try {
      return JSON.stringify(value);
    } catch (error) {
      return fallback;
    }
  }
  return String(value);
}

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}


function humanizeDisplayValue(value, fallback = "—") {
  const raw = displayValue(value, "").trim();
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();

  const exactLabels = {
    soc2: "SOC2",
    "soc 2": "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    aml: "AML",
    kyc: "KYC",
    fda: "FDA",
    osha: "OSHA",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    cloudflare: "Cloudflare",
    okta: "Okta",
    "google workspace": "Google Workspace",
    "risk queue": "Risk Queue",
    "risk queue default": "Risk Queue Default",
    "validation rerun": "Validation Rerun",
    "validation evidence link": "Validation Evidence",
    "remediation task evidence": "Remediation Evidence",
    "archive record": "Archive Record",
    "task scoped": "Task Scoped",
    "not linked": "Not Linked",
    "not loaded": "Not Loaded",
    "in progress": "In Progress",
    "ready for validation": "Ready for Validation",
    "ready to work": "Ready to Work",
    "awaiting validation": "Awaiting Validation",
    "awaiting approval": "Awaiting Approval",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return exactLabels[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function evidenceTone(value) {
  const normalized = clean(value).toLowerCase();
  if (["used", "complete", "fresh", "linked", "recorded", "verified"].includes(normalized)) return "ok";
  if (["missing", "stale", "incomplete", "failed", "missing_file"].includes(normalized)) return "danger";
  return "neutral";
}

function evidenceTitle(record = {}) {
  return humanizeDisplayValue(
    record.title ||
      record.name ||
      record.evidence_type ||
      record.usage_type ||
      record.source_type ||
      record.evidence_source,
    "Evidence record"
  );
}

function evidenceContextText(record = {}) {
  return displayValue(
    record.summary ||
      record.description ||
      record.notes ||
      record.message ||
      record.link_metadata?.message ||
      record.metadata?.message,
    "Task-scoped evidence is linked to this work item."
  );
}

export default function EvidenceExecutionTab({
  task = {},
  evidenceRecords = [],
  evidenceContext = null,
  evidenceLoading = false,
  evidenceError = "",
  onRefreshEvidence,
  onOpenTab,
}) {
  const rows = Array.isArray(evidenceRecords) ? evidenceRecords.filter(Boolean) : [];
  const counts = evidenceContext?.counts && typeof evidenceContext.counts === "object" ? evidenceContext.counts : {};
  const status = clean(evidenceContext?.status || task.evidence_state || (rows.length ? "linked" : "not_loaded"));
  const latestFreshness = rows.find((record) => clean(record.freshness_status || record.freshness))?.freshness_status || rows.find((record) => clean(record.freshness))?.freshness || "—";

  return (
    <>
      <ExecutionSection
        title="Task Evidence"
        description="Evidence linked to the selected task, control, resource, validation runs, and proof-layer archive records."
        badge={evidenceLoading ? "Loading" : humanizeDisplayValue(status, `${rows.length} record(s)`)}
        badgeTone={rows.length ? "ok" : "neutral"}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14 }}>
          <ExecutionMetricTile label="Evidence Records" value={rows.length} />
          <ExecutionMetricTile label="Validation Links" value={counts.validation_evidence_links ?? "—"} color="#60a5fa" />
          <ExecutionMetricTile label="Remediation Evidence" value={counts.remediation_task_evidence ?? "—"} color="#34d399" />
          <ExecutionMetricTile label="Latest Freshness" value={humanizeDisplayValue(latestFreshness)} color="#93c5fd" />
        </div>

        {evidenceError ? (
          <p style={{ color: "#fca5a5", fontSize: 13, lineHeight: 1.55, margin: 0 }}>
            <strong>Evidence load issue:</strong> {evidenceError}
          </p>
        ) : null}

        <p style={{ color: "#93c5fd", fontSize: 12.5, lineHeight: 1.5, margin: 0 }}>
          This tab is task-scoped. It intentionally excludes tenant-wide evidence records that do not match this task/control/resource.
        </p>

        <ExecutionActionRow
          actions={[
            {
              key: "refresh-evidence",
              label: evidenceLoading ? "Refreshing…" : "Refresh Evidence",
              onClick: onRefreshEvidence,
              disabled: evidenceLoading || !onRefreshEvidence,
              variant: "primary",
            },
            { key: "validation", label: "View Validation", onClick: () => onOpenTab?.("validation") },
            { key: "history", label: "View History", onClick: () => onOpenTab?.("history") },
          ]}
        />
      </ExecutionSection>

      <ExecutionSection
        title="Evidence Records"
        description="Task-scoped validation evidence, remediation evidence, and proof-layer archive matches."
        badge={`${rows.length} record(s)`}
        badgeTone={rows.length ? "ok" : "neutral"}
      >
        {evidenceLoading && !rows.length ? (
          <ExecutionEmptyState title="Loading task evidence" message="OASYS is loading task-scoped evidence context for this work item." />
        ) : null}

        {rows.length
          ? rows.map((record, index) => (
              <ExecutionContextCard
                key={displayValue(record.id || record.evidence_id || record.archive_id || record.validation_evidence_link_id, `evidence-${index}`)}
                title={evidenceTitle(record)}
                chips={[
                  { label: humanizeDisplayValue(record.status || record.verification_status, "Evidence"), tone: evidenceTone(record.status || record.verification_status) },
                  { label: humanizeDisplayValue(record.source_type, "task scoped"), tone: "default" },
                  { label: humanizeDisplayValue(record.evidence_source || record.source, "Source unknown"), tone: "neutral" },
                ]}
                meta={[
                  { label: "Evidence ID", value: record.evidence_id || record.id || record.archive_id, wrap: true },
                  { label: "Validation Run", value: record.validation_run_id, wrap: true },
                  { label: "Validation Result", value: record.validation_result_id, wrap: true },
                  { label: "Freshness", value: humanizeDisplayValue(record.freshness_status || record.freshness), wrap: true },
                  { label: "Completeness", value: humanizeDisplayValue(record.completeness_status || record.completeness), wrap: true },
                  { label: "Confidence", value: record.confidence_score || record.confidence || record.confidence_impact, wrap: true },
                ]}
                context={evidenceContextText(record)}
                actions={[
                  { key: "validation", label: "View Validation", onClick: () => onOpenTab?.("validation") },
                  { key: "history", label: "View History", onClick: () => onOpenTab?.("history") },
                ]}
              />
            ))
          : null}

        {!evidenceLoading && !rows.length ? (
          <ExecutionEmptyState
            title="No task-scoped evidence found"
            message="No validation evidence, remediation evidence, or proof-layer archive records matched this task/control/resource yet."
          />
        ) : null}
      </ExecutionSection>
    </>
  );
}
